
import { footer } from './en/footer';
import { navigation } from './en/navigation';
import { activities } from './en/activities';
import { hero } from './en/hero';
import { principles } from './en/principles';
import { easterCamp, christmasCamp, summerCamp, summerCampConfirmation } from './en/camps';
import { prices } from './en/prices';
import { montessori } from './en/montessori';
import { openHouse } from './en/openHouse';
import { extracurriculars } from './en/extracurriculars';
import { feedback } from "./en/feedback";
import { courses } from "./en/courses";
import { schoolCalendar } from './en/schoolCalendar';
import { birthdays } from './en/birthdays';
import { services } from './en/services';
import { admissions } from './en/admissions';
import { enrollmentForm } from './en/enrollmentForm';
import { enrollmentConfirmation } from './en/enrollmentConfirmation';
import { trustBadges } from './en/trustBadges';
import { valueProposition } from './en/valueProposition';
import { testimonials } from './en/testimonials';
import { ctaSection } from './en/ctaSection';
import { gallery } from './en/gallery';
import { howWeTeach } from './en/howWeTeach';
import { trilingualModel } from './en/trilingualModel';
import { neasc } from './en/neasc';
import { visitBanner } from './en/visitBanner';
import { renewalForm } from './en/renewalForm';
import { renewalConfirmation } from './en/renewalConfirmation';
import { openHouseRegistration } from './en/openHouseRegistration';
import { admin } from './en/admin';

export const en = {
  title: "Gándara International School",
  description: "Gándara International School, Montessori education in Vigo",
  navigation,
  hero,
  trustBadges,
  valueProposition,
  howWeTeach,
  trilingualModel,
  neasc,
  gallery,
  activities,
  principles,
  testimonials,
  ctaSection,
  footer,
  easterCamp,
  christmasCamp,
  summerCamp,
  summerCampConfirmation,
  prices,
  montessori,
  openHouse,
  openHouseRegistration,
  extracurriculars,
  feedback,
  courses,
  schoolCalendar,
  birthdays,
  services,
  admissions,
  enrollmentForm,
  enrollmentConfirmation,
  renewalForm,
  renewalConfirmation,
  admin,
  visitBanner,
  trilingual: {
    title: "Trilingual Education",
    metaDescription: "Our trilingual approach at Gándara International School",
    bookVisit: "Book a Visit",
    intro: {
      p1: "At Gándara International School, we believe in providing a truly global education that prepares students for the connected world they will inherit.",
      p2: "Our trilingual program immerses students in Spanish, English, and Galician, creating natural opportunities for language acquisition and cultural understanding."
    },
    sections: {
      immersion: {
        title: "Natural Language Immersion",
        p1: "Rather than teaching languages as isolated subjects, our approach integrates languages throughout the school day. Children experience different languages in context, making connections and developing fluency naturally.",
        p2: "Each language has dedicated times and spaces, with native or highly proficient teachers creating an authentic linguistic environment."
      },
      languages: {
        title: "Three Languages, One Community",
        p1: "Spanish serves as our primary language of instruction, connecting students to our local community and Spanish culture.",
        p2: "English and Galician are integrated throughout the curriculum, with specific subjects and activities conducted in each language to ensure balanced exposure and practice."
      },
      benefits: {
        title: "The Multilingual Advantage",
        intro: "Research consistently shows that children who learn multiple languages from an early age develop significant cognitive advantages:",
        list: [
          "Enhanced problem-solving and creative thinking skills",
          "Better memory and information processing",
          "Greater cultural awareness and empathy",
          "Improved academic performance across all subjects",
          "Greater educational and career opportunities in the future"
        ],
        conclusion: "These benefits extend far beyond language skills, supporting holistic development and preparing students for success in an increasingly global society."
      },
      methodology: {
        title: "Our Approach",
        content: "Our trilingual methodology is designed with children's natural language acquisition capabilities in mind. Young children have an innate ability to absorb languages without the confusion often experienced by adults. Through immersive experiences, engaging activities, and consistent exposure, students develop confident communication skills in all three languages."
      },
      environment: {
        title: "A Supportive Environment",
        p1: "We create a positive, encouraging atmosphere where students feel safe practicing new languages without fear of making mistakes. Learning is active, contextual, and meaningful.",
        p2: "Our small class sizes ensure each child receives individualized attention and ample opportunities to practice speaking and listening in each language."
      }
    }
  },
  about: {
    title: "About Us",
    subtitle: "Meet our team of guides dedicated to the education and growth of each child",
    bookVisit: "Book a visit",
    story: {
      title: "Our Story",
      content: "Gándara was born in 2013 from the dream of two families seeking a different education for their children: a space where respect, nature, and freedom were the foundations of learning. What began as a small school on a property in Gondomar has become an NEASC-accredited international school, with a team of passionate guides and a community of families who share the same vision."
    },
    mission: {
      title: "Our Mission",
      content: "To accompany each child in their holistic development, respecting their natural rhythms and processes, in a natural setting where active learning, multilingual living, and curiosity drive each day."
    },
    values: {
      title: "Our Values",
      items: [
        { title: "Respect", description: "For each child's unique rhythms, emotions, and processes." },
        { title: "Autonomy", description: "We foster independence and confidence in one's own abilities." },
        { title: "Community", description: "Families, guides, and children grow together in a shared project." },
        { title: "Nature", description: "Our more than 5,000 m² property is a living classroom for learning and exploration." },
        { title: "Reinvestment", description: "Gándara does not distribute profits: every euro is reinvested in the school to offer the best possible education." }
      ]
    },
    video: {
      title: "See Our School"
    },
    teamTitle: "Our Team",
    photoComingSoon: "Photo coming soon",
    back: "Back",
    team: [
      {
        name: "Patricia Méndez",
        role: "Co-Director",
        description: "Together with Pablo I founded Barkibu, the project that allows us to support Gándara and where we look after animals and their families. We fell in love with Gándara because our daughters grow up here and we believe it is the best possible education: that is why we support it and accompany families day to day."
      },
      {
        name: "Pablo Pazos",
        role: "Co-Director",
        description: "Patricia and I are the founders of Barkibu, the project that allows us to support Gándara and where we look after animals and their families. We fell in love with Gándara as a family: our daughters study here and we believe this is the best possible education. That is why we support it and manage it to last for many years."
      },
      {
        name: "Iago Ferraz",
        role: "Founder, Head of Education and educator",
        description: "For fifteen years I dreamed of starting a different kind of school: a place of coexistence built on respect and love for children, in the middle of nature. Today that dream is Gándara, where I learn and enjoy every day with the best teachers: the children themselves. Being part of this makes me deeply grateful."
      },
      {
        name: "Verónica Álvarez",
        role: "Deputy Director",
        description: "Gándara is much more than a job to me: it is my second home. It makes me happy to be part of this little big family and to learn every day from the values in the air here: solidarity, freedom, integrity and creativity."
      },
      {
        name: "Alba Couse",
        role: "Preschool educator",
        description: "I am an Early Childhood teacher with a specialisation in Hearing and Language, currently taking the Montessori 0-6 master's at the International Montessori Institute. I first came to Gándara for my university placement and stayed: this year I accompany Preschool, with a special eye for high sensitivity and care."
      },
      {
        name: "Carmen Aguilar",
        role: "Cycle 1 educator",
        description: "I am a Primary school teacher specialising in English. My experience as a teacher and class tutor has allowed me to accompany children with different rhythms, interests and needs. I keep training in active methodologies and, especially, in the Montessori method. I face this new stage with great excitement and the commitment to accompany every child with sensitivity, respect and affection."
      },
      {
        name: "David Torres",
        role: "Cycle 2 educator and Science lead",
        description: "I am an oceanographer and science communicator: for more than ten years I have been bringing science closer to audiences of every age, with training in Montessori science, educational robotics and gamification. I love it when wonder at nature becomes the door to learning, from the sea to the vegetable garden."
      },
      {
        name: "Patricia Iglesias",
        role: "Preschool educator and Cycle 1 English",
        description: "A philologist by training and an educator by vocation: I came to education through my love of languages and stayed for the children. I have spent years teaching English to young children through play, with training in active pedagogies, Montessori and nature education, and an approach where the emotional bond always comes first."
      },
      {
        name: "Paula Alonso",
        role: "Lunch and Preschool educator",
        description: "I am an Early Childhood teacher and have spent my whole professional life between respectful education and nature: for years I coordinated the 3-6 area of a free-education project and ran a children's leisure centre. At Gándara I accompany lunchtime and Preschool, where the table is also school: eating calmly, talking and caring for each other."
      },
      {
        name: "Sandra Amigo",
        role: "Cycle 2 educator and English specialist",
        description: "I am a teacher specialising in foreign languages, trilingual in English, Spanish and Catalan, and a Forest School Teacher. I have coordinated English at schools with more than 250 students and educational projects at university level, always with active and Montessori methodologies. What drives me is building a better world through an education that fosters empathy and respect for everything around us."
      },
      {
        name: "Yoli Paz",
        role: "Cleaning and care of the spaces",
        description: "I make sure Gándara's spaces are clean and welcoming every day. A well-cared-for environment also educates."
      }
    ]
  }
};
